import { gql, useQuery } from '@apollo/client';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import { Alert } from '@apollo/orbit';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { assertUnreachableOrReturnDefault } from 'register-apollo-graphql-language/src/utils/assertUnreachable';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { Loading } from 'src/components/common/loading/Loading';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PlanProtectedArea } from 'src/components/planProtectedArea/PlanProtectedArea';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { DocsRouter } from 'src/lib/routers';

import { ChecksConfigPage } from '../checksPage/checksConfigPage/ChecksConfigPage';
import { CustomChecksConfigPage } from '../checksPage/customChecksConfigPage/CustomChecksConfigPage';
import { LinterConfigPage } from '../checksPage/linterConfigPage/LinterConfigPage';
import { ProposalChecksConfigGraphPage } from '../checksPage/proposalChecksConfigGraphPage/ProposalChecksConfigGraphPage';
import {
  GraphSettingsSlug,
  graphSettingsRouteConfig,
  variantSettingsRouteConfig,
} from '../routes';

import { APIKeysPage } from './apiKeysPage/APIKeysPage';
import { GeneralSettingsPage } from './generalPage/GeneralSettingsPage';
import { GraphSettingsMenu } from './GraphSettingsMenu';
import { AccessPage } from './permissionsPage/AccessPage';
import { ProposalsPage } from './proposalsPage/ProposalsPage';
import { AddNotificationButton } from './reportingPage/addNotificationButton/AddNotificationButton';
import { NotificationsSettingsPage } from './reportingPage/NotificationsSettingsPage';
import { SourceVariantsPage } from './variantsPage/SourceVariantsPage';

function isChecksTab(
  tab: GraphSettingsSlug,
): tab is Extract<GraphSettingsSlug, `checks${string}`> {
  return tab.startsWith('checks');
}
export const GraphSettingsLayout = () => {
  const {
    tab = 'general',
    graphId,
    graphVariant,
  } = useRouteParams(graphSettingsRouteConfig);
  const graphRef = useMemo(
    () => ({ graphId, graphVariant }),
    [graphId, graphVariant],
  );
  const [currentAccountId] = useCurrentAccountId();
  const location = useLocation();
  const { data, loading } = useQuery<
    GraphQLTypes.GraphSettingsLayoutQuery,
    GraphQLTypes.GraphSettingsLayoutQueryVariables
  >(
    gql`
      query GraphSettingsLayoutQuery($graphId: ID!) {
        service(id: $graphId) {
          id
          title
          variants {
            id
            hasSupergraphSchema
            latestLaunch {
              id
              build {
                result {
                  ... on BuildFailure {
                    errorMessages {
                      code
                      locations {
                        column
                        line
                      }
                      message
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: { graphId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const [l3MenuWidth, setL3MenuWidth] = useLocalStorage('graphSettingsL3Width');

  const hasSupergraphVariants = useMemo(() => {
    return !!data?.service?.variants.some((variant) => {
      const latestBuild = variant.latestLaunch?.build;
      const buildErrors =
        latestBuild?.result?.__typename === 'BuildFailure'
          ? latestBuild.result.errorMessages
          : [];
      return variant.hasSupergraphSchema || isMinLengthArray(1, buildErrors);
    });
  }, [data?.service?.variants]);

  useTrackL1PageView('graphSettingsL1', { graphRef });

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <PageLayout
        title={
          <PageTitle
            title="Settings"
            /**
             * Special case here - when we're on the notifications page, show
             * the "Add Notification" button.  If we end up with more branching
             * like this, I'd suggest we use a different composition strategy
             * to give control back to the page, i.e. have NotificationSettingsPage
             * (and siblings) render the PageLayout themselves.  Then this file
             * is more like a "GraphSettingsRoute" than a Layout, and it's
             * job is to decide which page to render rather then anything about
             * the layout.
             */
            rightSide={
              tab === 'reporting' ? (
                <PermissionGuard
                  graphId={graphId}
                  graphPermissions="canQueryIntegrations"
                  fallback={null}
                >
                  <AddNotificationButton />
                </PermissionGuard>
              ) : null
            }
            tabs={
              <>
                <HeaderTab
                  tabID={`This Variant (${graphVariant})`}
                  isActive={() => false}
                  to={variantSettingsRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: graphSettingsRouteConfig,
                  })}
                />
                <HeaderTab
                  tabID="This Graph"
                  to={location}
                  isActive={() => true}
                />
              </>
            }
          />
        }
        l3MenuProps={{
          minWidth: 140,
          maxWidth: 600,
          setWidth: setL3MenuWidth,
          width: l3MenuWidth,
        }}
        l3Menu={
          <GraphSettingsMenu
            graphRef={graphRef}
            hasSupergraphVariants={hasSupergraphVariants}
          />
        }
      >
        <div className="w-full space-y-4 py-4 pl-1 pr-4">
          {tab === 'general' ? (
            <PermissionGuard
              graphId={graphId}
              graphPermissions={['canQueryPublicInfo', 'canUpdateAvatar']}
              fallback={PermissionBlockCard}
            >
              <GeneralSettingsPage graphRef={graphRef} />
            </PermissionGuard>
          ) : tab === 'permissions' ? (
            <PermissionGuard
              graphId={graphId}
              graphPermissions={['canUpdateTitle', 'canUpdateAvatar']}
              fallback={PermissionBlockCard}
            >
              <AccessPage graphRef={graphRef} />
            </PermissionGuard>
          ) : tab === 'api-keys' ? (
            <PermissionGuard
              graphId={graphId}
              graphPermissions={['canQueryTokens']}
              fallback={PermissionBlockCard}
            >
              <APIKeysPage graphRef={graphRef} />
            </PermissionGuard>
          ) : tab === 'reporting' ? (
            <PermissionGuard
              graphId={graphId}
              graphPermissions={[
                'canQueryIntegrations',
                'canManageIntegrations',
              ]}
              fallback={PermissionBlockCard}
            >
              <NotificationsSettingsPage />
            </PermissionGuard>
          ) : tab === 'variants' ? (
            <PermissionGuard
              graphId={graphId}
              graphPermissions="canQueryStats"
              fallback={PermissionBlockCard}
            >
              <SourceVariantsPage graphRef={graphRef} />
            </PermissionGuard>
          ) : tab === 'proposals' ? (
            hasSupergraphVariants && (
              <PlanProtectedArea
                accountId={currentAccountId}
                redirectExpiredToOrg={true}
                requiresPlanCapability="proposals"
              >
                <PermissionGuard
                  graphId={graphId}
                  graphPermissions="canManageProposalSettings"
                  fallback={PermissionBlockCard}
                >
                  <ProposalsPage graphRef={graphRef} />
                </PermissionGuard>
              </PlanProtectedArea>
            )
          ) : isChecksTab(tab) ? (
            <PermissionGuard
              graphId={graphId}
              graphPermissions="canQueryCheckConfiguration"
              fallback={PermissionBlockCard}
            >
              {tab === 'checks_proposals' ? (
                <Alert status="info">
                  We recommend turning on proposal checks only for the variant
                  that most closely resembles the state of your main git branch.
                </Alert>
              ) : (
                <Alert status="info">
                  This configuration will be applied to all checks for the{' '}
                  <b>{data?.service?.title}</b> graph
                  {tab === 'checks_operations' && ', unless overridden'}.{' '}
                  <ClickableText
                    as="a"
                    href={DocsRouter.path('SchemaValidation')}
                    className="font-semibold underline"
                  >
                    Learn more <IconOutlink className="ml-0.5" />
                  </ClickableText>
                </Alert>
              )}
              {tab === 'checks' || tab === 'checks_operations' ? (
                <ChecksConfigPage graphId={graphId} />
              ) : tab === 'checks_custom_checks' ? (
                <CustomChecksConfigPage graphRef={graphRef} type="graph" />
              ) : tab === 'checks_linter' ? (
                <LinterConfigPage graphRef={graphRef} type="graph" />
              ) : tab === 'checks_proposals' ? (
                <ProposalChecksConfigGraphPage graphRef={graphRef} />
              ) : (
                assertUnreachableOrReturnDefault(tab, null)
              )}
            </PermissionGuard>
          ) : (
            assertUnreachableOrReturnDefault(tab, null)
          )}
        </div>
      </PageLayout>
    </>
  );
};
